import { useState } from 'react'
import {
  type LocationFilterBanner,
  parseLocCodes,
  useAdminLocationsConfig,
  useUpdateLocationsConfig,
} from '../../../features/admin/use-admin-locations'
import { AdminPage, adminError, Meta } from './admin-bits'
import { Btn, Inline, inputCls } from './controls'
import { mono, Panel } from './ui'

function LocField({
  banner,
  field,
  label,
  hint,
  saved,
}: {
  banner: LocationFilterBanner
  field: 'excludedLocs' | 'allowedLocs'
  label: string
  hint: string
  saved: string[]
}) {
  const update = useUpdateLocationsConfig()
  const [text, setText] = useState(saved.join(', '))
  const parsed = parseLocCodes(text)
  const dirty = parsed.join(',') !== saved.join(',')
  return (
    <div>
      <label className="block">
        <span className="mb-1 flex items-baseline gap-2 text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]">
          {label}
          <span className="normal-case tracking-normal opacity-80">{hint}</span>
        </span>
        <textarea
          className={`${inputCls} h-20 py-1.5`}
          style={mono}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <div className="mt-1.5 flex items-center justify-between gap-3">
        <span className="text-[11px] text-[var(--color-muted)]" style={mono}>
          {parsed.length} codes
        </span>
        <div className="flex items-center gap-3">
          {update.error ? <Inline tone="bad">{adminError(update.error)}</Inline> : null}
          {update.isSuccess && !dirty ? <Inline tone="ok">saved</Inline> : null}
          <Btn
            variant="primary"
            disabled={!dirty || update.isPending}
            onClick={() => update.mutate({ banner, [field]: parsed })}
          >
            {update.isPending ? 'Saving...' : 'Save'}
          </Btn>
        </div>
      </div>
    </div>
  )
}

export function SignalAdminLocations() {
  const cfg = useAdminLocationsConfig()
  const banners: [LocationFilterBanner, string][] = [
    ['dtlr', 'DTLR'],
    ['spc', 'SPC'],
  ]
  return (
    <AdminPage
      page="locations"
      title="Location filters"
      meta={<Meta>codes split on comma / space / newline</Meta>}
      loading={cfg.isLoading}
      error={!!cfg.error}
    >
      {banners.map(([b, label]) => (
        <Panel key={b} title={label}>
          <div className="grid gap-4 p-3 md:grid-cols-2">
            <LocField
              banner={b}
              field="excludedLocs"
              label="Excluded"
              hint="never shown"
              saved={cfg.data?.[b].excludedLocs ?? []}
            />
            <LocField
              banner={b}
              field="allowedLocs"
              label="Allowed"
              hint="when set, only these"
              saved={cfg.data?.[b].allowedLocs ?? []}
            />
          </div>
        </Panel>
      ))}
    </AdminPage>
  )
}
